import {
  attendanceOpen,
  isSessionFull,
  sessionAttendanceTaken,
  type AvailableSession,
  type OwnerOverride,
  type OwnerParticipant,
  type SessionAttendee,
  type SessionWaitingVisit,
} from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  AttendeeList,
  bookedAhead,
  sessionEnded,
  useSessionEnded,
  Button,
  ErrorState,
  SegmentedControl,
  Select,
  SkeletonList,
  showToast,
} from "../components/index.ts";
import { track } from "@teamna/web-client/analytics/web.ts";
import { NotBookedList } from "../attendance/not-booked.tsx";
import { ownerAddCopy } from "@teamna/web-client/api/copy.ts";
import { useAddParticipantToSession, useSetSessionStaff } from "@teamna/web-client/hooks/use-groups.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness, useOwnerSessionWaiting } from "@teamna/web-client/hooks/use-owner.ts";
import { useParticipants, useTeam } from "@teamna/web-client/hooks/use-setup.ts";
import type { UseQueryResult } from "@tanstack/react-query";
import { AddRefusal } from "./add-anyway.tsx";
import { runsBusiness } from "./office.tsx";
import { CoachChoice, teamOption } from "./timetable/Rail.tsx";
import { attendanceButtonLabel } from "./today/progress.ts";

/**
 * The session controls the desktop panel (`schedule-grid.tsx`) and the phone sheet (`schedule.tsx`)
 * share: who runs the session, who is coming and who came, and add a participant. One copy, so the
 * phone layout never falls behind the desktop one again.
 *
 * Each control reports a failure through `onError`, so the panel shows it in its own error slot.
 */

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * The numbers at the top of a session panel. Before the end: Booked and the places left. After
 * the end: Came first (everyone marked present, with the names that wait for a parent), then Booked
 * without the people added at the session who did not book. Older API builds send no `cameCount`:
 * then it stays as before.
 */
export function sessionFacts(session: AvailableSession, now: Date = new Date()): Array<{ label: string; value: string }> {
  const of = (count: number) => (session.capacity === null ? `${count} booked` : `${count} of ${session.capacity}`);
  if (sessionEnded(session, now) && session.cameCount !== undefined) {
    return [
      { label: "Came", value: String(session.cameCount) },
      { label: "Booked", value: of(bookedAhead(session)) },
    ];
  }
  return [
    { label: "Booked", value: of(session.bookedCount) },
    { label: "Spots left", value: session.spotsLeft === null ? "No limit" : String(session.spotsLeft) },
  ];
}

/**
 * The staff member on this session: the same chips (or the search picker for a large team) as the
 * desktop panel and the timetable. Not a native select: on iPhone Safari a select in the phone panel
 * (a modal dialog) did not open its list (#ai-feedback 2026-10-26). Only people who have an account
 * can run a session.
 */
export function SessionStaffChoice({
  businessId,
  session,
  onError,
}: {
  businessId: string;
  session: AvailableSession;
  onError: (error: unknown) => void;
}) {
  const { label } = useLabels();
  const team = useTeam(businessId);
  const setStaff = useSetSessionStaff(businessId);
  const Staff = label("staff", { capital: true });
  const options = [
    ...(team.data ?? []).flatMap((member) => (member.userId === null ? [] : [teamOption({ userId: member.userId, name: member.name, photoUrl: member.photoUrl ?? null })])),
    { value: "", label: "No one" },
  ];

  return (
    <div className="flex flex-col gap-2 border-b border-line py-3">
      <span className="text-[14px] text-ink-2">{Staff}</span>
      <CoachChoice
        label={`${label("staff")} for this session`}
        value={session.staffUserId ?? ""}
        options={options}
        disabled={setStaff.isPending}
        onChange={(userId) => {
          onError(null);
          setStaff.mutate(
            { sessionId: session.id, staffUserId: userId || null },
            {
              onSuccess: (row) =>
                showToast(row.staffName ? `${row.staffName} runs this session now.` : `This session has no ${label("staff")} now.`),
              onError,
            },
          );
        }}
      />
    </div>
  );
}

/**
 * Who is coming, and from the session's day on, who came. The marks are read only here: "Take
 * attendance" opens the cards or list flow (`/owner/schedule/:id/attendance`), the same one staff
 * use, so there is one way to mark and one check screen. Marks never move a balance.
 */
export function SessionRegister({
  session,
  attendees,
  waiting = [],
  timezone,
  onMessage,
  onTakeAttendance,
  sticky = false,
  hideList = false,
}: {
  session: AvailableSession;
  attendees: UseQueryResult<SessionAttendee[]>;
  /** The names that wait for a parent and came (no booking yet): they are on the list too. */
  waiting?: readonly SessionWaitingVisit[];
  /** The business's timezone: marks open on the session's day in its clock. */
  timezone: string | undefined;
  onMessage: (memberUserId: string) => void;
  /** Opens attendance where the page wants it (the Day view's panel). Missing: the attendance route. */
  onTakeAttendance?: () => void;
  /** Keep Take attendance at the bottom of the scrolling panel, so a long list never hides it. */
  sticky?: boolean;
  /** Only Take attendance: another list shows in place of who is coming (`SessionPeople`). */
  hideList?: boolean;
}) {
  const navigate = useNavigate();
  const canMark = session.status === "scheduled" && attendanceOpen(new Date(session.startsAt), new Date(), timezone);

  if (attendees.isLoading && !attendees.data) return hideList ? null : <SkeletonList rows={2} />;
  // A failed read shows on both tabs: with no roster there is no Take attendance, so the retry must stay in reach.
  if (attendees.error && !attendees.data) return <ErrorState error={attendees.error} onRetry={() => void attendees.refetch()} />;
  const rows = attendees.data ?? [];
  const confirmed = rows.filter((row) => row.status === "confirmed");
  // The names that wait for a parent came too (shared attendance.ts), as on the coach's Today.
  const taken = sessionAttendanceTaken({ roster: confirmed, waitingExtras: waiting });
  return (
    <>
      {/* Also with nobody booked: people come with no booking, and the attendance screen adds them. */}
      {canMark ? (
        <div className={sticky ? "sticky bottom-0 z-10 order-last -mx-3.5 -mb-3.5 border-t border-line bg-paper/95 px-3.5 py-3 backdrop-blur" : ""}>
          <Button
            variant={taken ? "secondary" : "primary"}
            fullWidth
            readOnlySafe
            className={sticky ? "" : "mb-3"}
            onClick={() => (onTakeAttendance ? onTakeAttendance() : void navigate({ to: "/owner/schedule/$sessionId/attendance", params: { sessionId: session.id } }))}
          >
            {attendanceButtonLabel(taken)}
          </Button>
        </div>
      ) : null}
      {hideList ? null : <AttendeeList attendees={rows} waiting={waiting} onMessage={onMessage} />}
    </>
  );
}

/**
 * The people of a session, in two tabs: Who is coming (the register and Add a participant) and Did
 * not book (2026-10-19): the swimmers of the level with no booking in it, those who usually come on
 * its day first. The desktop panel and the phone sheet share it. Take attendance shows on both tabs.
 * A cancelled or moved session has only Who is coming.
 */
export function SessionPeople({
  businessId,
  session,
  attendees,
  timezone,
  onMessage,
  onTakeAttendance,
  sticky = false,
  onError,
  headClassName,
}: {
  businessId: string;
  session: AvailableSession;
  attendees: UseQueryResult<SessionAttendee[]>;
  timezone: string | undefined;
  onMessage: (memberUserId: string) => void;
  onTakeAttendance?: () => void;
  sticky?: boolean;
  onError: (error: unknown) => void;
  /** Space around the tabs, as the place wants it. */
  headClassName: string;
}) {
  const { labels } = useLabels();
  const [tab, setTab] = useState<"coming" | "not-booked">("coming");
  const isLive = session.status === "scheduled";
  const notBooked = isLive && tab === "not-booked";
  // The same read as the bookings (one request): the names that wait for a parent and came.
  const waiting = useOwnerSessionWaiting(businessId, session.id);
  // After the end, the list is who came, not who is coming (#ai-feedback 2026-10-26).
  const listLabel = useSessionEnded(session.endsAt) ? "Who came" : "Who is coming";
  return (
    <>
      <div className={headClassName}>
        {isLive ? (
          <SegmentedControl
            label="People of this session"
            size="compact"
            value={tab}
            options={[
              { value: "coming", label: listLabel },
              { value: "not-booked", label: "Did not book" },
            ]}
            onChange={(next) => {
              setTab(next);
              if (next === "not-booked") track("session_not_booked_opened", {});
            }}
          />
        ) : (
          <p className="eyebrow text-ink-2">{listLabel}</p>
        )}
      </div>
      <SessionRegister session={session} attendees={attendees} waiting={waiting.data ?? []} timezone={timezone} onMessage={onMessage} onTakeAttendance={onTakeAttendance} sticky={sticky} hideList={notBooked} />
      {notBooked ? <NotBookedList businessId={businessId} sessionId={session.id} labels={labels} openClassNext={`Add a ${labels.participantSingular} on Who is coming.`} /> : null}
      {isLive && !notBooked ? <AddParticipantForm businessId={businessId} session={session} attendees={attendees.data ?? []} onError={onError} /> : null}
    </>
  );
}

/**
 * Add a participant to this session. It takes one session from their balance, like any booking.
 * When the add is refused for a package or payment reason, the owner sees why in their own words and
 * can add anyway, add for free, or tell the client (`AddRefusal`).
 */
export function AddParticipantForm({
  businessId,
  session,
  attendees,
  onError,
}: {
  businessId: string;
  session: AvailableSession;
  attendees: SessionAttendee[];
  onError: (error: unknown) => void;
}) {
  const { label } = useLabels();
  const { business } = useActiveBusiness();
  const participants = useParticipants(businessId);
  const addParticipant = useAddParticipantToSession(businessId);
  const [participantId, setParticipantId] = useState("");
  const navigate = useNavigate();
  const [refused, setRefused] = useState<{ participant: { id: string; name: string }; copy: NonNullable<ReturnType<typeof ownerAddCopy>> } | null>(null);
  const participantWord = label("participant");
  const runs = business !== null && runsBusiness(business);

  const onRoster = new Set(attendees.filter((row) => row.status === "confirmed").map((row) => row.participantId));
  const addable: OwnerParticipant[] = (participants.data ?? [])
    .filter((row) => !onRoster.has(row.id))
    .sort(
      (a, b) =>
        Number(b.groupId === session.groupId) - Number(a.groupId === session.groupId) ||
        a.displayName.localeCompare(b.displayName),
    );

  /** The family account of a participant: its page is where a payment is recorded. */
  const payerOf = (id: string) => (participants.data ?? []).find((row) => row.id === id)?.members[0]?.userId ?? null;

  const add = (participant: { id: string; name: string }, override?: OwnerOverride) => {
    onError(null);
    addParticipant.mutate(
      { sessionId: session.id, participantId: participant.id, override },
      {
        onSuccess: (result) => {
          setParticipantId("");
          setRefused(null);
          showToast(
            result.override === "owed"
              ? `${participant.name} is on this session and owes 1 session. The next package pays it.`
              : result.override === "free"
                ? `${participant.name} is on this session for free.`
                : `${participant.name} is on this session. ${plural(result.sessionsLeft, "session", "sessions")} left.`,
          );
        },
        onError: (error) => {
          const copy = ownerAddCopy(error, { name: participant.name, groupName: session.groupName });
          if (copy) setRefused({ participant, copy });
          else {
            setRefused(null);
            onError(error);
          }
        },
      },
    );
  };

  return (
    <div className="mt-5 rounded-card border border-line p-4">
      <p className="text-[14px] font-semibold">Add a {participantWord}</p>
      <p className="mt-0.5 text-[13px] text-ink-2">This takes one session from their package.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Select
          label={`${label("participant", { capital: true })} to add`}
          className="min-w-0 flex-1 basis-[180px]"
          value={participantId}
          onChange={(event) => {
            setParticipantId(event.target.value);
            setRefused(null);
          }}
        >
          <option value="">Choose a {participantWord}</option>
          {addable.map((row) => (
            <option key={row.id} value={row.id}>
              {row.displayName}
              {row.groupName ? ` (${row.groupName})` : ""}
            </option>
          ))}
        </Select>
        <Button
          variant="secondary"
          disabled={!participantId || isSessionFull(session) || refused !== null}
          loading={addParticipant.isPending && addParticipant.variables?.override === undefined}
          onClick={() => add({ id: participantId, name: addable.find((row) => row.id === participantId)?.displayName ?? participantWord })}
        >
          Add
        </Button>
      </div>
      {refused ? (
        <AddRefusal
          key={refused.participant.id}
          businessId={businessId}
          session={session}
          participant={refused.participant}
          refusal={refused.copy}
          canOverride={runs}
          canTell={runs}
          cardPayments={business?.cardPayments ?? false}
          bookingFor={business?.bookingFor}
          timezone={business?.timezone}
          adding={addParticipant.isPending ? (addParticipant.variables?.override ?? null) : null}
          onAdd={(override) => add(refused.participant, override)}
          onRecordPayment={payerOf(refused.participant.id) ? () => void navigate({ to: "/owner/parents/$id", params: { id: payerOf(refused.participant.id)! } }) : null}
          onClose={() => setRefused(null)}
        />
      ) : null}
      {isSessionFull(session) ? (
        <p className="mt-2 text-[13px] text-ink-2">This session is full. Move someone out first.</p>
      ) : null}
    </div>
  );
}
